import React, { useMemo } from "react";
import {
  AssignmentSuggestionCardList,
  AssignmentGroup,
} from "../../components/chatNewInterface/interfaceComponents/AssignmentSuggestionCardList/AssignmentSuggestionCardList";
import { AssignmentSuggestion } from "../../components/chatNewInterface/interfaceComponents/AssignmentSuggestionCard/AssignmentSuggestionCard";
import { useGetAssignmentsWithCourseQuery, useGetAllCompanionChatsForUserQuery } from "../../graphql/generated/graphql";
import { useAppConfig } from "../../AppConfigProvider";
import { useWorkspaceThreads } from "../../contexts/WorkspaceThreadsContext";

const GENERAL_WORKSPACE_ID = "general_chats";

interface AssignmentSuggestionCardContainerProps {
  onAssignmentPress: (assignment: AssignmentSuggestion & { courseId?: string; courseName?: string }) => void;
}

const AssignmentSuggestionCardContainer: React.FC<AssignmentSuggestionCardContainerProps> = ({ onAssignmentPress }) => {
  const appConfig = useAppConfig();
  const schoolId = appConfig?.axioSchools[0];
  const { activeWorkspaceId } = useWorkspaceThreads();

  const { data: chatsData } = useGetAllCompanionChatsForUserQuery({});
  const courseWorkspaces = chatsData?.getAllCompanionChatsForUser?.courseWorkspaces ?? [];

  const isGeneralWorkspace = !activeWorkspaceId || activeWorkspaceId === GENERAL_WORKSPACE_ID;

  // Get the course ID if we're in a course workspace ${userId}|${courseId}
  const courseId = useMemo(() => {
    if (isGeneralWorkspace) return null;
    const workspace = courseWorkspaces.find((ws) => ws.id === activeWorkspaceId);
    if (!workspace) {
      return null;
    }

    const key = workspace.key;

    if (!key || typeof key !== "string") {
      console.warn("Workspace key not available.");
      return null;
    }

    const parts = key.split("|");
    const extractedCourseId = parts.length === 2 ? parts[1] : null;
    return extractedCourseId;
  }, [activeWorkspaceId, courseWorkspaces, isGeneralWorkspace]);

  const { data, loading } = useGetAssignmentsWithCourseQuery({
    variables: {
      schoolId,
      searchQuery: "",
    },
    skip: !schoolId,
    fetchPolicy: "network-only",
  });

  const { title, groups, showCourseHeaders } = useMemo(() => {
    if (!data?.getAssignmentsWithCourse?.data) {
      return { title: "", groups: [], showCourseHeaders: true };
    }

    const assignments = data.getAssignmentsWithCourse.data;

    const now = new Date();
    const upcomingAssignments = assignments.filter((a) => {
      if (!a.due_date) return false;
      return new Date(a.due_date) > now;
    });

    upcomingAssignments.sort((a, b) => {
      const dateA = a.due_date ? new Date(a.due_date).getTime() : 0;
      const dateB = b.due_date ? new Date(b.due_date).getTime() : 0;
      return dateA - dateB;
    });

    if (isGeneralWorkspace) {
      const grouped: { [key: string]: AssignmentGroup } = {};

      upcomingAssignments.forEach((assignment) => {
        const courseName = assignment.courseName || "Other";
        if (!grouped[courseName]) {
          grouped[courseName] = {
            courseName,
            courseCode: assignment.courseCode,
            assignments: [],
          };
        }

        if (grouped[courseName].assignments.length < 3) {
          grouped[courseName].assignments.push({
            id: assignment.id,
            title: assignment.title,
            due_date: assignment.due_date,
          });
        }
      });

      return {
        title: "Create a thread from an upcoming assignment",
        groups: Object.values(grouped),
        showCourseHeaders: true,
      };
    } else {
      const courseAssignments = upcomingAssignments.filter((a) => a.courseId === courseId).slice(0, 3);

      if (courseAssignments.length === 0) {
        return { title: "", groups: [], showCourseHeaders: false };
      }

      const courseName = courseAssignments[0]?.courseName || "Course";

      return {
        title: `Create a thread from an upcoming assignment in ${courseName}`,
        groups: [
          {
            courseName,
            courseCode: null,
            assignments: courseAssignments.map((a) => ({
              id: a.id,
              title: a.title,
              due_date: a.due_date,
            })),
          },
        ],
        showCourseHeaders: false,
      };
    }
  }, [data, isGeneralWorkspace, courseId]);

  if (groups.length === 0 || loading) {
    return null;
  }

  const handleAssignmentPress = (assignment: AssignmentSuggestion) => {
    const fullAssignment = data?.getAssignmentsWithCourse?.data?.find((a) => a.id === assignment.id);
    onAssignmentPress({
      ...assignment,
      courseId: fullAssignment?.courseId,
      courseName: fullAssignment?.courseName,
    });
  };

  return (
    <AssignmentSuggestionCardList
      title={title}
      groups={groups}
      onAssignmentPress={handleAssignmentPress}
      showCourseHeaders={showCourseHeaders}
    />
  );
};

export default AssignmentSuggestionCardContainer;
